// src/pages/admin/Login/AdminLoginPage.jsx
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch.js'
import { login, clearError } from '../../../redux/slices/authSlice.js'
import SEO from '../../../components/seo/SEO.jsx'
import Input from '../../../components/common/Input.jsx'
import Button from '../../../components/common/Button.jsx'
import { Toaster } from 'react-hot-toast'

export default function AdminLoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { loading, isAuthenticated } = useAppSelector((state) => state.auth)

  const { register, handleSubmit, formState: { errors } } = useForm()

  const from = location.state?.from?.pathname || '/admin/cms'

  // Clear any previous errors on mount
  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  // Check if already authenticated
  useEffect(() => {
    const token = localStorage.getItem('token')
    const userStr = localStorage.getItem('user')

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr)
        if (user.role === 'admin' && isAuthenticated) {
          navigate(from, { replace: true })
        }
      } catch (e) {
        // Invalid data, clear it
        localStorage.removeItem('user')
        localStorage.removeItem('token')
      }
    }
  }, [isAuthenticated, navigate, from])

  const onSubmit = async (data) => {
    try {
      await dispatch(login(data)).unwrap()
      // Navigation will be handled by the useEffect above
    } catch (error) {
      // Error is handled by the slice with toast
      console.error('Login failed:', error)
    }
  }

  return (
    <>
      <SEO title="Admin Login" noindex={true} />
      <Toaster position="top-right" />

      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo */}
          <div className="flex justify-center">
            <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">IT</span>
            </div>
          </div>

          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Admin Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access the admin dashboard
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* Email Field */}
              <Input
                label="Email Address"
                type="email"
                autoComplete="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email address'
                  }
                })}
                error={errors.email?.message}
              />

              {/* Password Field */}
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
                error={errors.password?.message}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                loading={loading}
                className="w-full"
                size="large"
                disabled={loading}
              >
                Sign in
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}